import React, { useState, useRef } from 'react';
import Tool, { type ToolButtonComponent } from './Tool';
import ToolbarMenu from '../toolbars/ToolbarMenu';
import { ApplicationView, useController } from '@aha-app/mvc';
import ToolButton from '../toolbars/ToolButton';
import layoutPaddingSVG from '../assets/icons/layoutPadding.svg';
import layoutGapSVG from '../assets/icons/layoutGap.svg';
import layoutHorizontalUserSVG from '../assets/icons/layoutHorizontalUser.svg';
import layoutHorizontalParentSVG from '../assets/icons/layoutHorizontalParent.svg';
import layoutHorizontalChildrenSVG from '../assets/icons/layoutHorizontalChildren.svg';
import layoutVerticalUserSVG from '../assets/icons/layoutVerticalUser.svg';
import layoutVerticalParentSVG from '../assets/icons/layoutVerticalParent.svg';
import layoutVerticalChildrenSVG from '../assets/icons/layoutVerticalChildren.svg';
import layoutSizeSpacing from '../assets/icons/layoutSizeSpacing.svg';
import { svgToIcon } from '../utils/svg';
import ToolbarArea from '../toolbars/ToolbarArea';
import ToolbarRow from '../toolbars/ToolbarRow';
import WhiteboardController from '../controllers/WhiteboardController';
import { type Instance as TippyInstance } from 'tippy.js/index';
import customElementBoolAttr from '../utils/customElementBoolAttr';
import ToolbarVDivider from '../toolbars/ToolbarVDivider';
import WithHint from '../toolbars/WithHint';
import Tippy, { useSingleton } from '@tippyjs/react';
import { TOOLTIP_DELAY_GENERAL } from '../constants';
import ToolbarColumn from '../toolbars/ToolbarColumn';
import { ToggleButton } from './ToolLayoutOrientation';

interface PopupProps {
  selection: fabric.Object;
  reloadPopup: () => void;
}
const Popup: React.FC<PopupProps> = ApplicationView(({ selection }) => {
  const controller = useController(WhiteboardController);
  const [tippySingletonSource, singleton] = useSingleton();

  const handleSliderChange = (propertyName, newValue) => {
    selection.setOnObjects({ [propertyName]: newValue });
    Tool.toolActionComplete({ controller });
  };

  const [currentLayoutSizeHorizontal, setCurrentLayoutSizeHorizontal] =
    useState(selection.firstObject().get('layoutSizeHorizontal'));
  const [currentLayoutSizeVertical, setCurrentLayoutSizeVertical] = useState(
    selection.firstObject().get('layoutSizeVertical')
  );
  const [currentLayoutGap, setCurrentLayoutGap] = useState(
    selection.firstObject().get('layoutGap')
  );

  const showParentFill = selection.firstObject().showParentFill();

  return (
    <ToolbarMenu columns={3}>
      <ToolbarColumn>
        <Tippy
          singleton={tippySingletonSource}
          placement='top'
          delay={TOOLTIP_DELAY_GENERAL}
        />
        <div style={{ width: 120 }}>
          <ToolbarArea>
            <WithHint name='Padding' singleton={singleton}>
              {svgToIcon(layoutPaddingSVG)}
            </WithHint>
            <div style={{ width: '80%' }}>
              <aha-slider
                ticks='[0,1,2,3,5,10,20,30,50,100]'
                start-value={Math.round(
                  selection.firstObject().get('layoutPadding')
                )}
                onInput={event =>
                  handleSliderChange(
                    'layoutPadding',
                    // @ts-expect-error selectedTickValue is not typed here
                    event.target.selectedTickValue
                  )
                }
                onMouseUp={() => Tool.toolActionComplete({ controller })}
              />
            </div>
          </ToolbarArea>
          <ToolbarArea>
            <WithHint name='Gap' singleton={singleton}>
              {svgToIcon(layoutGapSVG)}
            </WithHint>
            <div style={{ width: '80%' }}>
              <aha-slider
                // @ts-expect-error disabled is not an attribute for the component, but the styles use it.
                disabled={customElementBoolAttr(currentLayoutGap === 'auto')}
                ticks='[0,1,2,3,5,10,20,30,50,100]'
                start-value={Math.round(currentLayoutGap)}
                onInput={event => {
                  // @ts-expect-error selectedTickValue is not typed here
                  const newValue = event.target.selectedTickValue;
                  setCurrentLayoutGap(newValue);
                  selection.setOnObjects({ layoutGap: newValue });
                }}
                onMouseUp={() => Tool.toolActionComplete({ controller })}
              />
            </div>
          </ToolbarArea>
        </div>
        <ToolbarVDivider />
        <ToolbarRow>
          <ToggleButton
            name='Manual horizontal sizing'
            singleton={singleton}
            controller={controller}
            selection={selection}
            currentValue={currentLayoutSizeHorizontal}
            setCurrentValue={setCurrentLayoutSizeHorizontal}
            buttonProperty='layoutSizeHorizontal'
            buttonValue='user'
            buttonSvg={layoutHorizontalUserSVG}
          />
          <ToggleButton
            name='Fit contents horizontally'
            singleton={singleton}
            controller={controller}
            selection={selection}
            currentValue={currentLayoutSizeHorizontal}
            setCurrentValue={setCurrentLayoutSizeHorizontal}
            buttonProperty='layoutSizeHorizontal'
            buttonValue='children'
            buttonSvg={layoutHorizontalChildrenSVG}
          />
          <ToggleButton
            name='Fit frame horizontally'
            singleton={singleton}
            controller={controller}
            selection={selection}
            currentValue={currentLayoutSizeHorizontal}
            setCurrentValue={setCurrentLayoutSizeHorizontal}
            buttonProperty='layoutSizeHorizontal'
            buttonValue='parent'
            buttonSvg={layoutHorizontalParentSVG}
            disabled={!showParentFill}
          />
        </ToolbarRow>
        <ToolbarRow>
          <ToggleButton
            name='Manual vertical sizing'
            singleton={singleton}
            controller={controller}
            selection={selection}
            currentValue={currentLayoutSizeVertical}
            setCurrentValue={setCurrentLayoutSizeVertical}
            buttonProperty='layoutSizeVertical'
            buttonValue='user'
            buttonSvg={layoutVerticalUserSVG}
          />
          <ToggleButton
            name='Fit contents vertically'
            singleton={singleton}
            controller={controller}
            selection={selection}
            currentValue={currentLayoutSizeVertical}
            setCurrentValue={setCurrentLayoutSizeVertical}
            buttonProperty='layoutSizeVertical'
            buttonValue='children'
            buttonSvg={layoutVerticalChildrenSVG}
          />
          <ToggleButton
            name='Fit frame vertically'
            singleton={singleton}
            controller={controller}
            selection={selection}
            currentValue={currentLayoutSizeVertical}
            setCurrentValue={setCurrentLayoutSizeVertical}
            buttonProperty='layoutSizeVertical'
            buttonValue='parent'
            buttonSvg={layoutVerticalParentSVG}
            disabled={!showParentFill}
          />
        </ToolbarRow>
      </ToolbarColumn>
    </ToolbarMenu>
  );
});

// Configure all of the layout settings.
class ToolLayoutSizeSpacing extends Tool {
  static toolName = 'Size and spacing';

  static buttonComponent() {
    const Button: ToolButtonComponent = ApplicationView(
      ({ selection, tool }) => {
        const tippyRef = useRef<TippyInstance>();

        return (
          <ToolButton
            popup={
              <Popup
                selection={selection}
                reloadPopup={() => {
                  tippyRef.current?.hide();
                  setTimeout(() => {
                    tippyRef.current?.show();
                    tippyRef.current?.popperInstance?.forceUpdate();
                  }, 200);
                }}
              />
            }
            tippyRef={tippyRef}
            id='layoutSettings'
            tool={tool}
            maxWidth={400}
          >
            {svgToIcon(layoutSizeSpacing)}
          </ToolButton>
        );
      }
    );

    return Button;
  }
}

export default ToolLayoutSizeSpacing;
